// /admin/postulaciones-porteros
// Gestión de postulaciones recibidas vía /quiero-ser-portero.
// Workflow: pendiente → contactada → aprobada (crea cuenta) | rechazada

import { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import AdminLayout from '../../components/AdminLayout'
import { supabase } from '../../lib/supabase'

const ESTADO_PILL = {
  pendiente:  { bg: '#fef3c7', color: '#92400e', label: '⏳ Pendiente' },
  contactada: { bg: '#dbeafe', color: '#1e40af', label: '📞 Contactada' },
  aprobada:   { bg: '#d1fae5', color: '#065f46', label: '✅ Aprobada' },
  rechazada:  { bg: '#fee2e2', color: '#991b1b', label: '❌ Rechazada' },
}

const TURNO_LABEL = {
  manana: '🌅 Mañana', tarde: '🌇 Tarde', noche: '🌙 Noche',
  full: '⏰ Full-time', rotativo: '🔄 Rotativo',
}

const fmtFecha = (d) => d ? new Date(d).toLocaleString('es-UY', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—'

function generarSlug(direccion = '', barrio = '') {
  const limpio = (txt) => txt.toLowerCase()
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')
  const partes = [limpio(barrio), limpio(direccion).split('-').slice(-2).join('-')]
    .filter(Boolean)
  return partes.join('-').slice(0, 40) || 'portero-' + Math.random().toString(36).slice(2, 7)
}

// Normaliza una dirección para comparar (quita tildes, lowercase, solo letras+números)
function normalizar(d) {
  return (d || '').toLowerCase()
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]/g, ' ')
    .replace(/\s+/g, ' ').trim()
}

// % similitud entre 2 direcciones (Jaccard sobre tokens >= 3 chars)
function similitud(a, b) {
  const tokA = new Set(normalizar(a).split(' ').filter(t => t.length > 2))
  const tokB = new Set(normalizar(b).split(' ').filter(t => t.length > 2))
  if (tokA.size === 0 || tokB.size === 0) return 0
  let inter = 0
  tokA.forEach(t => { if (tokB.has(t)) inter += 1 })
  const union = tokA.size + tokB.size - inter
  return union > 0 ? inter / union : 0
}

export default function PostulacionesPorteros() {
  const [postulaciones, setPostulaciones] = useState([])
  const [clientesActivos, setClientesActivos] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchParams] = useSearchParams()
  // Deep-link desde dashboard: ?estado=pendiente
  const estadoInicial = (() => {
    const p = searchParams.get('estado')
    return ['pendiente', 'contactada', 'aprobada', 'rechazada'].includes(p) ? p : 'todas'
  })()
  const [filtro, setFiltro] = useState(estadoInicial)
  const [accion, setAccion] = useState(null)

  useEffect(() => { cargar() }, [])

  async function cargar() {
    setLoading(true)
    const [{ data: sols }, { data: cli }] = await Promise.all([
      supabase.from('porteros_solicitudes')
        .select('*, barrio:barrios(nombre)')
        .order('created_at', { ascending: false }),
      // Suscriptores activos para validar "vi un Limpack en mi edificio"
      supabase.from('clientes')
        .select('direccion, barrio_id')
        .eq('estado', 'activa'),
    ])
    setPostulaciones(sols || [])
    setClientesActivos(cli || [])
    setLoading(false)
  }

  // Para cada postulación, calcular si hay match con suscriptores reales
  function checkLimpackEnEdificio(sol) {
    if (clientesActivos.length === 0) return null
    // Match fuerte: dirección similar (>= 0.5)
    const matchDireccion = clientesActivos.filter(c => similitud(c.direccion, sol.direccion) >= 0.5).length
    // Match débil: mismo barrio
    const matchBarrio = clientesActivos.filter(c => c.barrio_id === sol.barrio_id).length
    return { matchDireccion, matchBarrio }
  }

  const filtradas = useMemo(() => {
    if (filtro === 'todas') return postulaciones
    return postulaciones.filter(p => p.estado === filtro)
  }, [postulaciones, filtro])

  const counts = useMemo(() => ({
    todas: postulaciones.length,
    pendiente: postulaciones.filter(p => p.estado === 'pendiente').length,
    contactada: postulaciones.filter(p => p.estado === 'contactada').length,
    aprobada: postulaciones.filter(p => p.estado === 'aprobada').length,
    rechazada: postulaciones.filter(p => p.estado === 'rechazada').length,
  }), [postulaciones])

  async function marcarContactada(id) {
    try {
      const { error } = await supabase.rpc('marcar_solicitud_contactada', { p_solicitud_id: id })
      if (error) throw error
      cargar()
    } catch (e) { alert('Error: ' + e.message) }
  }

  return (
    <AdminLayout>
      <div className="page">
        <h1>📨 Postulaciones de Porteros</h1>
        <p className="muted">Personas que se anotaron desde <code>/quiero-ser-portero</code>. Contactalas, visitalas y aprobá las que correspondan.</p>

        {/* Stats */}
        <div className="cards-grid" style={{ marginBottom: 16 }}>
          <div className="card">
            <h3>Total</h3>
            <div className="stat">{counts.todas}</div>
          </div>
          <div className="card">
            <h3>Pendientes</h3>
            <div className="stat" style={{ color: '#f59e0b' }}>{counts.pendiente}</div>
            <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>contactar urgente</div>
          </div>
          <div className="card">
            <h3>Contactadas</h3>
            <div className="stat" style={{ color: '#3b82f6' }}>{counts.contactada}</div>
            <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>visita pendiente</div>
          </div>
          <div className="card">
            <h3>Aprobadas</h3>
            <div className="stat" style={{ color: '#10B981' }}>{counts.aprobada}</div>
            <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>cuentas activas</div>
          </div>
        </div>

        {/* Filtros */}
        <div className="ap-filtros" style={{ marginBottom: 16 }}>
          {[
            { v: 'todas',      label: 'Todas',          c: counts.todas },
            { v: 'pendiente',  label: '⏳ Pendientes',  c: counts.pendiente },
            { v: 'contactada', label: '📞 Contactadas', c: counts.contactada },
            { v: 'aprobada',   label: '✅ Aprobadas',   c: counts.aprobada },
            { v: 'rechazada',  label: '❌ Rechazadas',  c: counts.rechazada },
          ].map(o => (
            <button key={o.v}
              className={`ap-filtro ${filtro === o.v ? 'is-active' : ''}`}
              onClick={() => setFiltro(o.v)}>
              {o.label} <em>{o.c}</em>
            </button>
          ))}
        </div>

        {loading && <div className="ap-loading">Cargando…</div>}

        {!loading && filtradas.length === 0 && (
          <div className="ap-empty">
            <div style={{ fontSize: 48 }}>📭</div>
            <h3>Sin postulaciones {filtro !== 'todas' && `en ${filtro}`}</h3>
            <p>Cuando alguien se postule desde <code>/quiero-ser-portero</code> va a aparecer acá.</p>
          </div>
        )}

        {/* Lista de cards */}
        <div className="pp-list">
          {filtradas.map(s => {
            const pill = ESTADO_PILL[s.estado]
            const wa = `https://wa.me/${s.celular.replace(/\D/g, '').replace(/^0/, '598')}?text=${encodeURIComponent(`Hola ${s.nombre.split(' ')[0]}, te escribo de Limpack 👋 Recibimos tu postulación para ser Portero Limpack. ¿Cuándo podemos coordinar una visita rápida?`)}`
            const check = checkLimpackEnEdificio(s)
            const fuenteEdificio = (s.fuente || '').toLowerCase().includes('vi a un limpack') || (s.fuente || '').toLowerCase().includes('limpack en mi edificio')
            return (
              <div key={s.id} className={`pp-card pp-card-${s.estado}`}>
                <div className="pp-head">
                  <div className="pp-avatar">{s.nombre.split(' ').map(n => n[0]).slice(0, 2).join('').toUpperCase()}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="pp-nombre">{s.nombre}</div>
                    <div className="pp-fecha">Postulación: {fmtFecha(s.created_at)}</div>
                  </div>
                  <span className="pp-pill" style={{ background: pill.bg, color: pill.color }}>{pill.label}</span>
                </div>

                <div className="pp-body">
                  <div className="pp-info">
                    <div className="pp-row">
                      <span className="pp-label">📍 Edificio</span>
                      <span>
                        {s.edificio_nombre && <strong>{s.edificio_nombre} · </strong>}
                        {s.direccion}<br/>
                        <span style={{ color: '#64748b', fontSize: 12 }}>
                          {s.barrio?.nombre} · <strong>{s.cant_unidades} apartamentos</strong>
                          {s.turno && ` · ${TURNO_LABEL[s.turno] || s.turno}`}
                        </span>
                      </span>
                    </div>
                    <div className="pp-row">
                      <span className="pp-label">📱 Contacto</span>
                      <span>
                        <a href={wa} target="_blank" rel="noopener" className="pp-wa-link">{s.celular} 💬</a><br/>
                        <span style={{ fontSize: 12, color: '#475569' }}>{s.email}</span>
                      </span>
                    </div>
                    {s.fuente && (
                      <div className="pp-row">
                        <span className="pp-label">📡 Origen</span>
                        <span>
                          {s.fuente}
                          {/* Verificación cruzada: si dice "vi un Limpack en mi edificio" debería haber suscriptores reales cerca */}
                          {fuenteEdificio && check && (
                            <div style={{ marginTop: 6 }}>
                              {check.matchDireccion > 0 ? (
                                <span className="pp-check pp-check-ok">
                                  ✅ Verificado: {check.matchDireccion} Limpack(s) suscriptos en una dirección parecida
                                </span>
                              ) : check.matchBarrio > 0 ? (
                                <span className="pp-check pp-check-warn">
                                  ⚠️ Hay {check.matchBarrio} suscriptos en el barrio, pero ninguno en una dirección parecida al edificio. Revisar.
                                </span>
                              ) : (
                                <span className="pp-check pp-check-bad">
                                  🚨 Dijo que vio un Limpack en su edificio, pero NO tenemos ningún suscriptor en esa dirección ni barrio. Verificar antes de aprobar.
                                </span>
                              )}
                            </div>
                          )}
                        </span>
                      </div>
                    )}
                    {s.mensaje && (
                      <div className="pp-row">
                        <span className="pp-label">💬 Mensaje</span>
                        <span style={{ fontStyle: 'italic', color: '#475569' }}>"{s.mensaje}"</span>
                      </div>
                    )}
                    {s.notas_admin && (
                      <div className="pp-row">
                        <span className="pp-label">📝 Notas internas</span>
                        <span style={{ background: '#fef3c7', padding: '4px 8px', borderRadius: 6, fontSize: 12 }}>{s.notas_admin}</span>
                      </div>
                    )}
                    {s.estado === 'rechazada' && s.rechazada_motivo && (
                      <div className="pp-row">
                        <span className="pp-label">❌ Motivo</span>
                        <span style={{ color: '#991b1b' }}>{s.rechazada_motivo}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Acciones según estado */}
                <div className="pp-actions">
                  {s.estado === 'pendiente' && (
                    <>
                      <a href={wa} target="_blank" rel="noopener" className="pp-btn pp-btn-wa">
                        💬 WhatsApp
                      </a>
                      <button className="pp-btn pp-btn-secondary"
                        onClick={() => marcarContactada(s.id)}>
                        📞 Marcar como contactada
                      </button>
                      <button className="pp-btn pp-btn-success"
                        onClick={() => setAccion({ tipo: 'aprobar', sol: s })}>
                        ✅ Aprobar
                      </button>
                      <button className="pp-btn pp-btn-danger"
                        onClick={() => setAccion({ tipo: 'rechazar', sol: s })}>
                        ❌ Rechazar
                      </button>
                    </>
                  )}
                  {s.estado === 'contactada' && (
                    <>
                      <a href={wa} target="_blank" rel="noopener" className="pp-btn pp-btn-wa">
                        💬 WhatsApp
                      </a>
                      <button className="pp-btn pp-btn-success"
                        onClick={() => setAccion({ tipo: 'aprobar', sol: s })}>
                        ✅ Aprobar y crear cuenta
                      </button>
                      <button className="pp-btn pp-btn-danger"
                        onClick={() => setAccion({ tipo: 'rechazar', sol: s })}>
                        ❌ Rechazar
                      </button>
                    </>
                  )}
                  {s.estado === 'aprobada' && s.portero_id_creado && (
                    <div style={{ fontSize: 13, color: '#065f46', padding: '8px 12px' }}>
                      ✅ Cuenta activa. Aprobada el {fmtFecha(s.aprobada_at)}.
                    </div>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {/* Modales */}
      {accion?.tipo === 'aprobar' && <ModalAprobar sol={accion.sol} onClose={() => { setAccion(null); cargar() }} />}
      {accion?.tipo === 'rechazar' && <ModalRechazar sol={accion.sol} onClose={() => { setAccion(null); cargar() }} />}

      <Estilos />
    </AdminLayout>
  )
}

function ModalAprobar({ sol, onClose }) {
  const [slug, setSlug] = useState(generarSlug(sol.direccion, sol.barrio?.nombre))
  const [pass, setPass] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [resultado, setResultado] = useState(null)

  async function aprobar() {
    setLoading(true); setError(null)
    try {
      const { data, error } = await supabase.rpc('aprobar_solicitud_portero', {
        p_solicitud_id: sol.id,
        p_slug: slug.trim(),
        p_password_temp: pass.trim() || null,
      })
      if (error) throw error
      setResultado(data?.[0])
    } catch (e) {
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }

  function copiar(texto) {
    navigator.clipboard.writeText(texto)
  }

  if (resultado) {
    // Seguridad 2026-06-04: el password NO se manda por WhatsApp (queda en el
    // historial del chat de ambas partes). Solo mandamos el link de entrada
    // y avisamos que las credenciales van por email aparte. El admin ve el
    // password en pantalla y lo comparte por canal seguro si es necesario.
    const wa = `https://wa.me/${sol.celular.replace(/\D/g, '').replace(/^0/, '598')}?text=${encodeURIComponent(
`¡Hola ${sol.nombre.split(' ')[0]}! 🎉

Tu cuenta de Portero Limpack ya está activa.

🔗 Entrá acá: ${resultado.login_url}
📧 Tu email de acceso: ${resultado.email}

Te mandamos las credenciales por email aparte. Cambiá la contraseña en tu primer login desde tu perfil.

Cualquier duda me avisás. ¡Bienvenido al equipo!`
    )}`

    return (
      <div className="pp-modal-bg" onClick={onClose}>
        <div className="pp-modal" onClick={e => e.stopPropagation()}>
          <h2>🎉 Cuenta creada</h2>
          <p style={{ color: '#475569', fontSize: 14 }}>
            La cuenta de <strong>{sol.nombre}</strong> está activa. Pasale las credenciales por WhatsApp:
          </p>

          <div className="pp-cred-box">
            <div>
              <strong>Email:</strong> {resultado.email}
              <button className="pp-copy-btn" onClick={() => copiar(resultado.email)}>copiar</button>
            </div>
            <div>
              <strong>Contraseña:</strong> <code>{resultado.password_generada}</code>
              <button className="pp-copy-btn" onClick={() => copiar(resultado.password_generada)}>copiar</button>
            </div>
            <div>
              <strong>URL:</strong> <a href={resultado.login_url} target="_blank" rel="noopener">{resultado.login_url}</a>
            </div>
          </div>

          <div className="pp-modal-actions">
            <a href={wa} target="_blank" rel="noopener" className="pp-btn pp-btn-wa pp-btn-large">
              💬 Mandar credenciales por WhatsApp
            </a>
            <button className="pp-btn pp-btn-secondary" onClick={onClose}>Cerrar</button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="pp-modal-bg" onClick={onClose}>
      <div className="pp-modal" onClick={e => e.stopPropagation()}>
        <h2>✅ Aprobar postulación</h2>
        <p style={{ color: '#475569', fontSize: 14 }}>
          Vas a crear la cuenta de portero para <strong>{sol.nombre}</strong>.
          Después le pasamos las credenciales por WhatsApp.
        </p>

        <div className="pp-form-row">
          <label>Slug del portero <small>(va en el link único)</small></label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontSize: 13, color: '#64748b', whiteSpace: 'nowrap' }}>limpack.uy/?ref=</span>
            <input value={slug} onChange={e => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
              placeholder="pocitos-2424" />
          </div>
          <small style={{ color: '#94a3b8' }}>Solo minúsculas, números y guiones. Ej: pocitos-2424</small>
        </div>

        <div className="pp-form-row">
          <label>Contraseña temporal <small>(opcional, se genera automática si lo dejás vacío)</small></label>
          <input value={pass} onChange={e => setPass(e.target.value)}
            placeholder="Dejá vacío para auto-generar" />
        </div>

        {error && <div className="pp-error">⚠️ {error}</div>}

        <div className="pp-modal-actions">
          <button className="pp-btn pp-btn-success pp-btn-large" onClick={aprobar} disabled={loading}>
            {loading ? 'Creando…' : '✅ Crear cuenta'}
          </button>
          <button className="pp-btn pp-btn-secondary" onClick={onClose} disabled={loading}>Cancelar</button>
        </div>
      </div>
    </div>
  )
}

function ModalRechazar({ sol, onClose }) {
  const [motivo, setMotivo] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  async function rechazar() {
    setLoading(true); setError(null)
    try {
      const { error } = await supabase.rpc('rechazar_solicitud_portero', {
        p_solicitud_id: sol.id,
        p_motivo: motivo.trim() || null,
      })
      if (error) throw error
      onClose()
    } catch (e) {
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="pp-modal-bg" onClick={onClose}>
      <div className="pp-modal" onClick={e => e.stopPropagation()}>
        <h2>❌ Rechazar postulación</h2>
        <p style={{ color: '#475569', fontSize: 14 }}>
          Vas a rechazar la postulación de <strong>{sol.nombre}</strong>.
          El motivo queda registrado en el sistema (no se le envía).
        </p>

        <div className="pp-form-row">
          <label>Motivo (opcional, interno)</label>
          <textarea value={motivo} onChange={e => setMotivo(e.target.value)}
            rows={3} placeholder="Ej: edificio fuera de zona / no es portero real / volumen sospechoso…" />
        </div>

        {error && <div className="pp-error">⚠️ {error}</div>}

        <div className="pp-modal-actions">
          <button className="pp-btn pp-btn-danger pp-btn-large" onClick={rechazar} disabled={loading}>
            {loading ? 'Rechazando…' : '❌ Rechazar postulación'}
          </button>
          <button className="pp-btn pp-btn-secondary" onClick={onClose} disabled={loading}>Cancelar</button>
        </div>
      </div>
    </div>
  )
}

function Estilos() {
  return (
    <style>{`
      /* Filtros chips */
      .ap-filtros { display: flex; gap: 6px; flex-wrap: wrap; }
      .ap-filtro {
        padding: 8px 14px; border: 1px solid #e5e7eb; background: #fff;
        border-radius: 999px; cursor: pointer; font-size: 13px; font-weight: 600;
        color: #475569; display: inline-flex; align-items: center; gap: 6px;
        font-family: inherit;
      }
      .ap-filtro em { font-style: normal; opacity: .7; font-size: 11px;
        background: rgba(0,0,0,.05); padding: 1px 7px; border-radius: 999px; }
      .ap-filtro.is-active { background: #1a3461; color: #fff; border-color: #1a3461; }
      .ap-filtro.is-active em { background: rgba(255,255,255,.2); }
      .ap-filtro:hover:not(.is-active) { background: #f1f5f9; }
      .ap-loading { padding: 40px; text-align: center; color: #6b7280; }
      .ap-empty {
        padding: 60px 20px; text-align: center; background: #fff;
        border: 1px dashed #cbd5e1; border-radius: 12px; color: #6b7280;
      }
      .ap-empty h3 { color: #1a3461; margin: 16px 0 8px; }

      .pp-list { display: flex; flex-direction: column; gap: 12px; }
      .pp-card {
        background: #fff; border: 1px solid #e5e7eb; border-radius: 12px;
        padding: 16px;
      }
      .pp-card-pendiente { border-left: 4px solid #f59e0b; }
      .pp-card-contactada { border-left: 4px solid #3b82f6; }
      .pp-card-aprobada { border-left: 4px solid #10B981; }
      .pp-card-rechazada { border-left: 4px solid #ef4444; opacity: .7; }

      .pp-head { display: flex; align-items: center; gap: 12px; margin-bottom: 14px; }
      .pp-avatar {
        width: 44px; height: 44px; border-radius: 50%;
        background: #1a3461; color: #fff;
        display: flex; align-items: center; justify-content: center;
        font-weight: 800; font-size: 14px; flex-shrink: 0;
      }
      .pp-nombre { font-weight: 800; color: #1a3461; font-size: 16px; }
      .pp-fecha { font-size: 11px; color: #94a3b8; margin-top: 2px; }
      .pp-pill { padding: 4px 10px; border-radius: 999px; font-size: 11px; font-weight: 700; white-space: nowrap; }

      .pp-info { display: flex; flex-direction: column; gap: 8px; }
      .pp-row { display: grid; grid-template-columns: 110px 1fr; gap: 10px; font-size: 13px; line-height: 1.5; }
      .pp-label { font-size: 11px; color: #6b7280; text-transform: uppercase; font-weight: 700; letter-spacing: .04em; padding-top: 2px; }

      .pp-wa-link { color: #25d366; font-weight: 700; text-decoration: none; }
      .pp-wa-link:hover { text-decoration: underline; }

      .pp-check { display: inline-block; padding: 4px 10px; border-radius: 8px;
        font-size: 11px; font-weight: 700; line-height: 1.4; }
      .pp-check-ok { background: #d1fae5; color: #065f46; }
      .pp-check-warn { background: #fef3c7; color: #92400e; }
      .pp-check-bad { background: #fee2e2; color: #991b1b; }

      .pp-actions {
        display: flex; gap: 6px; flex-wrap: wrap;
        margin-top: 14px; padding-top: 12px; border-top: 1px dashed #e2e8f0;
      }
      .pp-btn {
        padding: 8px 14px; border: 0; border-radius: 8px;
        font-weight: 700; font-size: 13px; cursor: pointer;
        text-decoration: none;
        display: inline-flex; align-items: center; gap: 5px;
      }
      .pp-btn-wa { background: #25d366; color: #fff; }
      .pp-btn-success { background: #10B981; color: #fff; }
      .pp-btn-danger { background: #fee2e2; color: #991b1b; border: 1px solid #fca5a5; }
      .pp-btn-secondary { background: #fff; color: #475569; border: 1px solid #d1d5db; }
      .pp-btn-large { min-height: 44px; padding: 12px 20px; font-size: 14px; }

      .pp-modal-bg {
        position: fixed; inset: 0; z-index: 1000;
        background: rgba(15,31,61,.55); backdrop-filter: blur(4px);
        display: flex; align-items: center; justify-content: center;
        padding: 16px;
      }
      .pp-modal {
        background: #fff; border-radius: 16px;
        max-width: 480px; width: 100%; max-height: 90vh;
        overflow-y: auto; padding: 24px;
        box-shadow: 0 16px 48px rgba(15,31,61,.3);
      }
      .pp-modal h2 { color: #1a3461; margin: 0 0 12px; font-size: 20px; }
      .pp-form-row { margin: 14px 0; }
      .pp-form-row label {
        display: block; font-size: 12px; font-weight: 700; color: #475569;
        margin-bottom: 5px; text-transform: uppercase; letter-spacing: .03em;
      }
      .pp-form-row label small { font-weight: 400; text-transform: none; color: #94a3b8; margin-left: 4px; }
      .pp-form-row input, .pp-form-row textarea {
        width: 100%; box-sizing: border-box;
        padding: 10px 12px; border: 1px solid #d1d5db; border-radius: 8px;
        font-size: 14px; font-family: inherit;
      }
      .pp-error { background: #fee2e2; border: 1px solid #fca5a5; color: #991b1b;
        padding: 10px 12px; border-radius: 8px; font-size: 13px; margin: 10px 0; }
      .pp-modal-actions { display: flex; gap: 8px; margin-top: 20px; flex-wrap: wrap; }

      .pp-cred-box {
        background: #f0fdf4; border: 1px solid #bbf7d0;
        border-radius: 10px; padding: 14px; margin: 14px 0;
        font-size: 13px; line-height: 2;
      }
      .pp-cred-box code { background: #fff; padding: 2px 6px; border-radius: 4px; font-size: 13px; }
      .pp-copy-btn {
        background: #1a3461; color: #fff; border: 0; border-radius: 4px;
        padding: 2px 8px; margin-left: 6px; font-size: 10px; cursor: pointer;
        font-weight: 700;
      }
    `}</style>
  )
}
